import React, { useEffect, useRef, useState } from 'react';

import { FormattedMessage, useIntl } from '../../../util/reactIntl';
import { listingPhotoAlt } from '../../../util/listingSeo';
import { Modal, ResponsiveImage } from '../../../components';

import css from './GalleryModal.module.css';

const variantSet = ['scaled-small', 'scaled-medium', 'scaled-large', 'scaled-xlarge'];

/**
 * GalleryModal
 *
 * Full-screen modal showing all photos stacked vertically.
 * Supports keyboard navigation: ESC closes, Arrow keys navigate between images.
 */
const GalleryModal = props => {
  const {
    images = [],
    isOpen,
    onClose,
    initialIndex = 0,
    onManageDisableScrolling,
    imageAltTitle,
    intl: intlProp,
  } = props;
  const intl = intlProp || useIntl();
  const itemRefs = useRef([]);
  const containerRef = useRef(null);
  const [activeIndex, setActiveIndex] = useState(initialIndex);

  // Sync activeIndex when modal opens (or initialIndex changes).
  useEffect(() => {
    if (isOpen) setActiveIndex(initialIndex);
  }, [isOpen, initialIndex]);

  // Smooth scroll to initial index after modal opens
  useEffect(() => {
    if (!isOpen) return;
    const t = setTimeout(() => {
      const node = itemRefs.current[initialIndex];
      if (node && typeof node.scrollIntoView === 'function') {
        node.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
      // Focus container for keyboard nav
      if (containerRef.current && typeof containerRef.current.focus === 'function') {
        containerRef.current.focus();
      }
    }, 50);
    return () => clearTimeout(t);
  }, [isOpen, initialIndex]);

  // Keyboard navigation
  useEffect(() => {
    if (!isOpen) return;
    const handleKey = e => {
      if (e.key === 'Escape') {
        if (typeof onClose === 'function') onClose();
        return;
      }
      const total = images.length;
      if (total === 0) return;
      let nextIndex = null;
      if (e.key === 'ArrowRight' || e.key === 'ArrowDown') {
        nextIndex = Math.min(activeIndex + 1, total - 1);
      } else if (e.key === 'ArrowLeft' || e.key === 'ArrowUp') {
        nextIndex = Math.max(activeIndex - 1, 0);
      }
      if (nextIndex !== null && nextIndex !== activeIndex) {
        e.preventDefault();
        setActiveIndex(nextIndex);
        const node = itemRefs.current[nextIndex];
        if (node && typeof node.scrollIntoView === 'function') {
          node.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      }
    };
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [isOpen, activeIndex, images.length, onClose]);

  const closeMessage = intl.formatMessage({ id: 'SectionPhotoMosaic.closeGallery' });

  const altFor = i => {
    const img = images[i];
    if (img && img.alt) return img.alt;
    return listingPhotoAlt(intl, { title: imageAltTitle, index: i, count: images.length });
  };

  return (
    <Modal
      id="ListingPage.photoMosaicGallery"
      isOpen={isOpen}
      onClose={onClose}
      onManageDisableScrolling={onManageDisableScrolling || (() => {})}
      usePortal
      containerClassName={css.modalContainer}
      contentClassName={css.modalContent}
      closeButtonMessage={closeMessage}
    >
      <div
        className={css.galleryWrapper}
        ref={containerRef}
        tabIndex={-1}
        role="dialog"
        aria-label={intl.formatMessage({ id: 'SectionPhotoMosaic.showAllPhotos' })}
      >
        <div className={css.galleryHeader}>
          <span className={css.galleryCounter}>
            <FormattedMessage
              id="SectionPhotoMosaic.photoCounter"
              values={{ current: activeIndex + 1, total: images.length }}
            />
          </span>
        </div>
        <div className={css.gallerySlides}>
          {images.map((img, i) => (
            <div
              key={img.id?.uuid || i}
              ref={el => (itemRefs.current[i] = el)}
              className={css.gallerySlide}
            >
              <ResponsiveImage
                rootClassName={css.gallerySlideImage}
                image={img}
                alt={altFor(i)}
                variants={variantSet}
                sizes="(max-width: 1024px) 100vw, 1024px"
              />
            </div>
          ))}
        </div>
      </div>
    </Modal>
  );
};

export default GalleryModal;
